// src/pages/CRMPanel/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../../layouts/AppLayout';
import apiClient from '../../lib/apiClient';
import './styles.css';

const TEMP_COLOR = { hot:'#f97316', warm:'#eab308', cold:'#60a5fa' };

export default function CRMPanelPage() {
  const [campaigns, setCampaigns]   = useState([]);
  const [selected, setSelected]     = useState('');
  const [leads, setLeads]           = useState([]);
  const [summary, setSummary]       = useState(null);
  const [newMsg, setNewMsg]         = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [loading, setLoading]       = useState(false);
  const [sending, setSending]       = useState(false);
  const [toast, setToast]           = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // Carrega campanhas do usuário para filtro
  useEffect(() => {
    const load = async () => {
      try {
        const r = await apiClient.request('/campaigns', { method: 'GET' });
        const d = await r.json();
        if (d.success && d.data) setCampaigns(d.data.slice(0, 20));
      } catch {}
    };
    load();
  }, []);

  const loadLeads = useCallback(async (campaignId) => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const r = await apiClient.request(`/lead/messages/${campaignId}`, { method: 'GET' });
      const d = await r.json();
      if (d.success) { setLeads(d.leads); setSummary(d.summary); }
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (selected) loadLeads(selected);
  }, [selected, loadLeads]);

  const handleSendLead = async () => {
    if (!newMsg.trim() || !selected) {
      showToast('Selecione uma campanha e escreva uma mensagem.');
      return;
    }
    setSending(true);
    try {
      const r = await apiClient.request('/lead/message', {
        method: 'POST',
        body: JSON.stringify({ campaign_id: selected, message: newMsg }),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`Lead classificado: ${d.lead.temperature === 'hot' ? 'Quente' : d.lead.temperature === 'warm' ? 'Morno' : 'Frio'}`);
        setNewMsg('');
        loadLeads(selected);
      }
    } catch { showToast('Erro ao processar lead'); }
    finally { setSending(false); }
  };

  const handleReply = async (leadId) => {
    const reply = window.prompt('Digite sua resposta para o lead:');
    if (!reply) return;
    try {
      const r = await apiClient.request(`/lead/${leadId}/reply`, {
        method: 'POST',
        body: JSON.stringify({ reply }),
      });
      const d = await r.json();
      if (d.success) { showToast('Resposta registrada!'); loadLeads(selected); }
    } catch {}
  };

  return (
    <AppLayout title="CRM IA de Leads" subtitle="Classifique e responda leads com inteligência artificial" fullWidth>
      {toast && <div className="crm-toast">{toast}</div>}

      {/* Selector de campanha */}
      <div className="crm-campsel">
        <label className="crm-label">Campanha</label>
        <div className="crm-campsel__row">
          <input className="crm-input" placeholder="ID da campanha ou cole aqui"
            value={campaignFilter} onChange={e => { setCampaignFilter(e.target.value); setSelected(e.target.value); }} />
          {campaigns.length > 0 && (
            <select className="crm-select" value={selected}
              onChange={e => { setSelected(e.target.value); setCampaignFilter(e.target.value); }}>
              <option value="">-- Selecionar campanha --</option>
              {campaigns.map(c => (
                <option key={c._id || c.id} value={c._id || c.id}>{c.name || c._id}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Novo lead manual */}
      <div className="crm-new-lead">
        <label className="crm-label">Simular mensagem de lead</label>
        <div className="crm-new-lead__row">
          <textarea className="crm-textarea" rows={3}
            placeholder="Ex: 'Quero saber mais sobre o cardápio do restaurante'"
            value={newMsg} onChange={e => setNewMsg(e.target.value)} />
          <button className="crm-btn crm-btn--primary" onClick={handleSendLead} disabled={sending}>
            {sending ? 'Analisando…' : 'Classificar com IA'}
          </button>
        </div>
      </div>

      {/* Resumo */}
      {summary && (
        <div className="crm-summary">
          <div className="crm-summary__card crm-summary__card--total">
            <div className="crm-summary__val">{summary.total}</div>
            <div className="crm-summary__lbl">Total</div>
          </div>
          <div className="crm-summary__card crm-summary__card--hot">
            <div className="crm-summary__val">{summary.hot}</div>
            <div className="crm-summary__lbl">Quente</div>
          </div>
          <div className="crm-summary__card crm-summary__card--warm">
            <div className="crm-summary__val">{summary.warm}</div>
            <div className="crm-summary__lbl">Morno</div>
          </div>
          <div className="crm-summary__card crm-summary__card--cold">
            <div className="crm-summary__val">{summary.cold}</div>
            <div className="crm-summary__lbl">Frio</div>
          </div>
        </div>
      )}

      {/* Lista de leads */}
      {loading ? (
        <div className="crm-loading">Carregando leads…</div>
      ) : leads.length === 0 && selected ? (
        <div className="crm-empty">Nenhum lead ainda. Aguardando mensagens da campanha.</div>
      ) : leads.map(lead => (
        <div key={lead._id || lead.id} className={`crm-lead-card crm-lead-card--${lead.temperature}`}>
          <div className="crm-lead-card__top">
            <span className="crm-lead-temp" style={{ color: TEMP_COLOR[lead.temperature] }}>
              {lead.temperature === 'hot' ? 'Quente' : lead.temperature === 'warm' ? 'Morno' : 'Frio'}
            </span>
            {lead.qualified && <span className="crm-qualified">Qualificado</span>}
            {lead.replied && <span className="crm-replied">Respondido</span>}
          </div>

          <div className="crm-lead-card__msg">
            <strong>Lead:</strong> {lead.message}
          </div>

          <div className="crm-lead-card__ai">
            <div className="crm-ai-label">Resposta sugerida pela IA:</div>
            <div className="crm-ai-text">{lead.ai_suggested_reply}</div>
          </div>

          {!lead.replied && (
            <button className="crm-btn crm-btn--reply" onClick={() => handleReply(lead._id || lead.id)}>
              Responder
            </button>
          )}

          {lead.reply_text && (
            <div className="crm-reply-box">
              <strong>Sua resposta:</strong> {lead.reply_text}
            </div>
          )}
        </div>
      ))}
    </AppLayout>
  );
}
